import { normalizeText } from "./offerHelpers";

/**
 * En la lista de citas del panel viven dos cosas distintas.
 *
 *   · **Citas de verdad** (`booking`): alguien pidió hora en un taller y hay
 *     alguien esperándole. Se confirman, se cambian, se cancelan.
 *   · **Recordatorios** (`calendar`, `suggestion`): la revisión que toca por
 *     kilómetros, la renovación del seguro, la sugerencia del asesor. No hay
 *     cita con nadie: son avisos de que convendría pedirla.
 *
 * Van en la misma lista porque en Operaciones se enseñan juntos, y eso está
 * bien. Lo que no vale es contarlos juntos: el número del lateral decía **9**
 * con una cita y ocho recordatorios, así que quien lo mira busca ocho citas que
 * no existen.
 *
 * La regla vive aquí, al lado de donde se pone el `source`, para que quien
 * añada un origen nuevo tenga delante las dos cosas.
 */
export const ORIGENES_DE_RECORDATORIO = ["calendar", "suggestion"];

/**
 * Si es una cita con alguien.
 *
 * Sin `source` también cuenta: es lo que traían las citas antes de que hubiera
 * recordatorios en esta lista, y las viejas no pueden dejar de contarse por
 * eso.
 */
export function esCitaDeVerdad(item) {
  const origen = item?.source;
  return !origen || origen === "booking";
}

export function esRecordatorio(item) {
  return ORIGENES_DE_RECORDATORIO.includes(item?.source);
}

/** Cuántas citas hay de verdad. Es lo que va en el número del lateral. */
export function cuantasCitas(items = []) {
  return (Array.isArray(items) ? items : []).filter(esCitaDeVerdad).length;
}

export function buildUserDashboardModel({
  savedComparisons = [],
  userAppointments = [],
  userMaintenances = [],
  userInsurances = [],
  userValuations = [],
  userVehicleStates = [],
  result = null,
  sellAiResult = null,
  sellAnswers = {},
  sellListingResult = null,
}) {
  const dashboardSavedComparisons = Array.isArray(savedComparisons)
    ? savedComparisons.slice(0, 3)
    : [];

  const advisorAppointments = result
    ? [
        {
          id: "advisor-follow-up",
          source: "suggestion",
          title: "Revisión personalizada PopCar",
          meta: `${normalizeText(result?.solucion_principal?.titulo) || "Tu plan de movilidad"} · pendiente de reservar`,
          status: "Pendiente",
        },
      ]
    : [];

  const maintenanceAppointments = Array.isArray(userMaintenances)
    ? userMaintenances
        .map((item) => {
          const vehiclePlate = normalizeText(item?.vehiclePlate);
          const vehicleLabel = vehiclePlate || normalizeText(item?.vehicleTitle) || "Vehículo";
          const scheduleLabel = normalizeText(item?.scheduledAt);
          const notes = normalizeText(item?.notes);

          return {
            id: normalizeText(item?.id),
            source: "calendar",
            type: "maintenance",
            title: normalizeText(item?.title) || `Mantenimiento · ${vehicleLabel}`,
            meta: [vehicleLabel, notes].filter(Boolean).join(" · "),
            status: normalizeText(item?.status) || "Pendiente",
            requestedAt: scheduleLabel,
          };
        })
        .filter((item) => item.id)
    : [];

  const insuranceAppointments = Array.isArray(userInsurances)
    ? userInsurances
        .map((item) => {
          const vehicleLabel = normalizeText(item?.vehiclePlate) || normalizeText(item?.vehicleTitle) || "Vehículo";
          const provider = normalizeText(item?.provider);
          const notes = normalizeText(item?.notes);
          const renewalAt = normalizeText(item?.renewalAt);

          return {
            id: normalizeText(item?.id),
            source: "calendar",
            type: "insurance",
            title: provider ? `Seguro · ${provider}` : `Seguro · ${vehicleLabel}`,
            meta: [vehicleLabel, notes].filter(Boolean).join(" · "),
            status: normalizeText(item?.status) || "Activo",
            requestedAt: renewalAt,
          };
        })
        .filter((item) => item.id)
    : [];

  const dashboardAppointments = [
    ...userAppointments.map((a) => ({ ...a, source: "booking" })),
    ...maintenanceAppointments,
    ...insuranceAppointments,
    ...advisorAppointments,
  ].slice(0, 12);

  const persistedValuations = Array.isArray(userValuations)
    ? userValuations
        .map((item) => ({
          id: normalizeText(item?.id),
          title: normalizeText(item?.title) || normalizeText(item?.vehicleTitle) || "Vehiculo en valoracion",
          meta: normalizeText(item?.meta) || normalizeText(item?.report),
          status: normalizeText(item?.status) || "Ultima tasacion disponible",
          estimateValue: item?.estimateValue != null ? Number(item.estimateValue) : null,
          createdAt: normalizeText(item?.createdAt),
          vehicleTitle: normalizeText(item?.vehicleTitle),
          /*
           * De qué coche es.
           *
           * Se caía aquí. El servidor lo manda y la ficha del coche lo busca,
           * pero este mapeador de en medio no lo copiaba, así que la ficha
           * decía «la tasación, sin hacer» con la tasación hecha y el precio a
           * la vista dos pantallas más allá.
           */
          vehicleId: normalizeText(item?.vehicleId),
          // Donde quedo el PDF, para poder ofrecer la descarga.
          pdfPath: normalizeText(item?.pdfPath),
        }))
        .filter((item) => item.id)
        .slice(0, 6)
    : [];

  const derivedValuations = sellAiResult
    ? [
        {
          id: "sell-valuation",
          title:
            sellAnswers?.brand && sellAnswers?.model
              ? `${sellAnswers.brand} ${sellAnswers.model}`
              : "Vehiculo en valoracion",
          meta:
            normalizeText(sellAiResult?.report) ||
            "Tasacion generada a partir del estado, kilometraje y demanda estimada.",
          status: sellListingResult ? "Tasacion vinculada a venta activa" : "Ultima tasacion disponible",
        },
      ]
    : [];

  const dashboardValuations = [...persistedValuations, ...derivedValuations].slice(0, 6);

  const persistedSections = {
    owned: [],
    sold: [],
    "active-sale": [],
  };

  if (Array.isArray(userVehicleStates)) {
    userVehicleStates.forEach((item) => {
      const state = normalizeText(item?.state).toLowerCase();
      const isListed = item?.isListed === true;

      const title = normalizeText(item?.title)
        || `${normalizeText(item?.brand)} ${normalizeText(item?.model)}`.trim()
        || "Vehiculo";

      const extraMeta = [normalizeText(item?.year), normalizeText(item?.notes)].filter(Boolean).join(" · ");

      const makeItem = (status) => ({
        id: normalizeText(item?.vehicleId),
        title,
        meta: extraMeta,
        status,
      });

      // Comprados: only vehicles explicitly in state "owned"
      if (state === "owned") {
        persistedSections.owned.push(makeItem("Vehiculo disponible"));
      } else if (state === "sold") {
        persistedSections.sold.push(makeItem("Operacion cerrada"));
      }

      // Marketplace listing — active_sale (legacy) OR is_listed=true
      if (isListed) {
        persistedSections["active-sale"].push(makeItem("Publicado en seguimiento"));
      }
    });
  }

  const userVehicleSections = [
    {
      key: "owned",
      title: "Comprados",
      items: persistedSections.owned,
      empty: "Todavía no has marcado vehículos como comprados.",
    },
    {
      key: "sold",
      title: "Vendidos",
      items: persistedSections.sold,
      empty: "Aún no tienes operaciones de venta cerradas.",
    },
    {
      key: "active-sale",
      title: "Activos en venta",
      items: [
        ...persistedSections["active-sale"],
        ...(
        sellAnswers?.brand && sellAnswers?.model
          ? [
              {
                title: `${sellAnswers.brand} ${sellAnswers.model}`,
                meta: `${sellAnswers.year || "Año pendiente"} · ${sellAnswers.sellerType === "profesional" ? "Venta profesional" : "Venta particular"}`,
                status: sellListingResult ? "Publicado en seguimiento" : "Borrador listo para publicar",
              },
            ]
          : []
        ),
      ],
      empty: "No hay anuncios activos en venta ahora mismo.",
    },
  ];

  return {
    dashboardSavedComparisons,
    dashboardAppointments,
    dashboardValuations,
    userVehicleSections,
  };
}
